import { resolveDatasetAccentColor } from "datasets/utils/color";
import { resolveAccentPaletteColor } from "~/lib/accentPalette";
import { cn } from "~/lib/utils";
import { DatasetIcon } from "~/pages/manifold-nextgen/components/datasets/DatasetIcon";
import { ProjectIcon } from "~/pages/manifold-nextgen/components/projects/ProjectIcon";
import { resourceIcon } from "~/pages/manifold-nextgen/components/resourceIcon";
import { SearchHit } from "~/services/search";
import { HitDetails } from "./useHitDetails";

const SIZES = {
  sm: { tile: "size-6 rounded-md", icon: 14, glyph: "size-3.5" },
  md: { tile: "size-8 rounded-lg", icon: 16, glyph: "size-4" },
} as const;

interface HitTileProps {
  hit: SearchHit;
  details: HitDetails | undefined;
  size: keyof typeof SIZES;
}

/** The entity's icon on a tile tinted with its accent colour, neutral until that is known. */
export const HitTile = ({ hit, details, size }: HitTileProps) => {
  const { tile, icon, glyph } = SIZES[size];
  const tint = tileTint(hit, details);
  return (
    <span
      className={cn(
        "flex shrink-0 items-center justify-center",
        tile,
        !tint && "bg-primary/10 text-primary"
      )}
      style={tint ? { backgroundColor: tint } : undefined}
      aria-hidden="true"
    >
      {hit.entity_type === "dataset" ? (
        <DatasetIcon
          // A dataset's accent is never null; the empty string is the default colour.
          dataset={details && { accent_color: details.accentColor ?? "" }}
          size={icon}
        />
      ) : hit.entity_type === "project" ? (
        <ProjectIcon project={details && { accent_color: details.accentColor }} size={icon} />
      ) : (
        resourceIcon(hit.entity_type, glyph)
      )}
    </span>
  );
};

/** The accent palette's tint, or none for a type without colours. */
function tileTint(hit: SearchHit, details: HitDetails | undefined): string | undefined {
  if (!details) {
    return undefined;
  }
  switch (hit.entity_type) {
    case "dataset":
      return resolveDatasetAccentColor(details.accentColor ?? undefined).bg;
    case "project":
      return resolveAccentPaletteColor(details.accentColor ?? undefined).bg;
    case "conversation":
      return undefined;
  }
}
